import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../contexts/AuthContext';
import { assetAPI, employeeAPI, companyAPI, settingsAPI } from '../../lib/api';
import { createImage } from '../../lib/imageUtils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../../components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { DataTable, useDataTable } from '../../components/ui/data-table';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import ModuleDisabled from '../../components/shared/ModuleDisabled';
import { formatIndianCurrency, cn } from '../../lib/utils';
import {
    Loader2,
    Plus,
    Monitor,
    HardDrive,
    Keyboard,
    Smartphone,
    Wifi,
    Package,
    Search,
    Upload,
    Download,
    UserPlus,
    UserMinus,
    Wrench,
    History,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock,
    BarChart3,
    TrendingUp,
    Pencil,
    Hammer,
    Check,
    ChevronsUpDown,
} from 'lucide-react';

const ASSET_CATEGORIES = [
    { value: 'HARDWARE', label: 'Hardware', icon: Monitor },
    { value: 'SOFTWARE', label: 'Software', icon: HardDrive },
    { value: 'PERIPHERAL', label: 'Peripheral', icon: Keyboard },
    { value: 'NETWORK', label: 'Network', icon: Wifi },
    { value: 'MOBILE', label: 'Mobile', icon: Smartphone },
    { value: 'OTHER', label: 'Other', icon: Package },
];

// Map icon names to components
const ICON_MAP = {
    Monitor, HardDrive, Keyboard, Wifi, Smartphone, Package,
};

const ASSET_STATUSES = [
    { value: 'AVAILABLE', label: 'Available', color: 'bg-green-500' },
    { value: 'ASSIGNED', label: 'Assigned', color: 'bg-blue-500' },
    { value: 'IN_REPAIR', label: 'In Repair', color: 'bg-yellow-500' },
    { value: 'RETIRED', label: 'Retired', color: 'bg-gray-500' },
    { value: 'LOST', label: 'Lost', color: 'bg-red-500' },
    { value: 'DISPOSED', label: 'Disposed', color: 'bg-gray-400' },
];

const ASSET_CONDITIONS = [
    { value: 'EXCELLENT', label: 'Excellent' },
    { value: 'GOOD', label: 'Good' },
    { value: 'FAIR', label: 'Fair' },
    { value: 'POOR', label: 'Poor' },
];

const MAINTENANCE_TYPES = [
    { value: 'REPAIR', label: 'Repair' },
    { value: 'UPGRADE', label: 'Upgrade' },
    { value: 'ROUTINE', label: 'Routine' },
    { value: 'INSPECTION', label: 'Inspection' },
];

function AssetManagement() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [assets, setAssets] = useState([]);
    const [stats, setStats] = useState(null);
    const [requests, setRequests] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [company, setCompany] = useState(null);
    const [activeTab, setActiveTab] = useState('assets');
    const [assetCategories, setAssetCategories] = useState(ASSET_CATEGORIES);

    // DataTable state
    const { rowSelection, setRowSelection, columnVisibility, setColumnVisibility } = useDataTable();

    // Filters
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [totalAssets, setTotalAssets] = useState(0);
    const pageSize = 20;

    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [showRequestModal, setShowRequestModal] = useState(false);

    // Selected item
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [assetHistory, setAssetHistory] = useState([]);
    const [maintenanceHistory, setMaintenanceHistory] = useState([]);

    // Form states
    const [formData, setFormData] = useState({});
    const [assignData, setAssignData] = useState({ employee_id: '', notes: '' });
    const [maintenanceData, setMaintenanceData] = useState({});
    const [requestData, setRequestData] = useState({ category: '', description: '', priority: 'NORMAL', justification: '' });
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRequestDetailModal, setShowRequestDetailModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const { dialogProps, confirm } = useConfirmation();

    const fetchAssets = useCallback(async () => {
        try {
            const params = { page, page_size: pageSize };
            if (search) params.search = search;
            if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
            if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;

            const response = await assetAPI.getAssets(params);
            setAssets(response.data.assets);
            setTotalAssets(response.data.total);
        } catch (error) {
            console.error('Error fetching assets:', error);
        }
    }, [page, search, categoryFilter, statusFilter]);

    const fetchStats = async () => {
        try {
            const response = await assetAPI.getStats();
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchRequests = async () => {
        try {
            const response = await assetAPI.getRequests({});
            setRequests(response.data);
        } catch (error) {
            console.error('Error fetching requests:', error);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await employeeAPI.getEmployees({ page_size: 500 });
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const fetchAssetCategories = async () => {
        try {
            const response = await settingsAPI.getAssetCategories();
            if (Array.isArray(response.data) && response.data.length > 0) {
                // Filter and transform API categories to match format
                const validCats = response.data.filter(cat => typeof cat.name === 'string');
                if (validCats.length > 0) {
                    const dynamicCats = validCats.map(cat => ({
                        value: cat.name.toUpperCase().replace(/\s+/g, '_'),
                        label: cat.name,
                        icon: ICON_MAP[cat.icon] || Package,
                    }));
                    setAssetCategories(dynamicCats);
                }
            }
        } catch (error) {
            // Use default categories on error
            console.log('Using default asset categories');
        }
    };

    const fetchCompany = async () => {
        try {
            const response = await companyAPI.getCurrent();
            setCompany(response.data);
        } catch (error) {
            console.error('Error fetching company:', error);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchAssets(), fetchStats(), fetchRequests(), fetchEmployees(), fetchCompany(), fetchAssetCategories()]);
            setLoading(false);
        };
        loadData();
    }, [fetchAssets]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    // Check module access
    if (company && !company.modules?.assets) {
        return <ModuleDisabled moduleName="IT Asset Management" />;
    }

    const handleAddAsset = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...formData, company_id: user.company_id };
            // Clean up vendor - only send if has actual values
            if (payload.vendor) {
                const hasVendorData = Object.values(payload.vendor).some(v => v && v.trim && v.trim() !== '');
                if (!hasVendorData) {
                    delete payload.vendor;
                }
            }
            // Clean up numeric fields - remove empty strings
            const numericFields = ['purchase_price', 'license_seats', 'useful_life_years', 'salvage_value'];
            numericFields.forEach(field => {
                if (payload[field] === '' || payload[field] === null) {
                    delete payload[field];
                }
            });
            await assetAPI.createAsset(payload);
            toast.success('Asset created successfully');
            setShowAddModal(false);
            setFormData({});
            fetchAssets();
            fetchStats();
        } catch (error) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to create asset';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateAsset = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...formData };
            // Clean up vendor - only send if has actual values
            if (payload.vendor) {
                const hasVendorData = Object.values(payload.vendor).some(v => v && v.trim && v.trim() !== '');
                if (!hasVendorData) {
                    delete payload.vendor;
                }
            }
            // Clean up numeric fields - remove empty strings
            const numericFields = ['purchase_price', 'license_seats', 'useful_life_years', 'salvage_value'];
            numericFields.forEach(field => {
                if (payload[field] === '' || payload[field] === null) {
                    delete payload[field];
                }
            });
            await assetAPI.updateAsset(selectedAsset.id, payload);
            toast.success('Asset updated successfully');
            setShowEditModal(false);
            setFormData({});
            setSelectedAsset(null);
            fetchAssets();
            fetchStats();
        } catch (error) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to update asset';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteAsset = async (asset) => {
        const confirmed = await confirm({
            title: 'Delete Asset',
            description: `Are you sure you want to delete "${asset.name}" (${asset.asset_tag})? This action cannot be undone.`,
            confirmText: 'Delete',
            variant: 'destructive'
        });

        if (confirmed) {
            try {
                await assetAPI.deleteAsset(asset.id);
                toast.success('Asset deleted successfully');
                fetchAssets();
                fetchStats();
            } catch (error) {
                const detail = error.response?.data?.detail;
                const msg = typeof detail === 'string' ? detail :
                    Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to delete asset';
                toast.error(msg);
            }
        }
    };

    const handleAssignAsset = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await assetAPI.assignAsset(selectedAsset.id, assignData);
            toast.success('Asset assigned successfully');
            setShowAssignModal(false);
            setAssignData({ employee_id: '', notes: '' });
            setSelectedAsset(null);
            fetchAssets();
            fetchStats();
        } catch (error) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to assign asset';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUnassignAsset = async (asset) => {
        const confirmed = await confirm({
            title: 'Unassign Asset',
            description: `Are you sure you want to unassign "${asset.name}" from ${asset.assigned_to_name}?`,
            confirmText: 'Unassign',
            variant: 'destructive'
        });

        if (confirmed) {
            try {
                await assetAPI.unassignAsset(asset.id);
                toast.success('Asset unassigned successfully');
                fetchAssets();
                fetchStats();
            } catch (error) {
                const detail = error.response?.data?.detail;
                const msg = typeof detail === 'string' ? detail :
                    Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to unassign asset';
                toast.error(msg);
            }
        }
    };

    const handleViewHistory = async (asset) => {
        setSelectedAsset(asset);
        try {
            const [historyRes, maintenanceRes] = await Promise.all([
                assetAPI.getHistory(asset.id),
                assetAPI.getMaintenanceHistory(asset.id)
            ]);
            setAssetHistory(historyRes.data);
            setMaintenanceHistory(maintenanceRes.data);
            setShowHistoryModal(true);
        } catch (error) {
            toast.error('Failed to fetch asset history');
        }
    };

    const handleAddMaintenance = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await assetAPI.addMaintenance(selectedAsset.id, maintenanceData);
            toast.success('Maintenance record added');
            setShowMaintenanceModal(false);
            setMaintenanceData({});
            setSelectedAsset(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add maintenance record');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBulkUpload = async (e) => {
        e.preventDefault();
        if (!uploadFile) return;

        setSubmitting(true);
        const formDataUpload = new FormData();
        formDataUpload.append('file', uploadFile);

        try {
            const response = await assetAPI.bulkUpload(formDataUpload);
            toast.success(response.data.message);
            setShowBulkUploadModal(false);
            setUploadFile(null);
            fetchAssets();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload assets');
        } finally {
            setSubmitting(false);
        }
    };

    const handleApproveRequest = async (request) => {
        try {
            await assetAPI.approveRequest(request.id);
            toast.success('Request approved');
            fetchRequests();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve request');
        }
    };

    const handleRejectRequest = async (request) => {
        const confirmed = await confirm({
            title: 'Reject Request',
            description: `Are you sure you want to reject this asset request from ${request.employee_name}?`,
            confirmText: 'Reject',
            variant: 'destructive'
        });

        if (confirmed) {
            try {
                await assetAPI.rejectRequest(request.id, 'Request rejected by admin');
                toast.success('Request rejected');
                fetchRequests();
                fetchStats();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to reject request');
            }
        }
    };

    const handleCreateRequest = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await assetAPI.createRequest(requestData);
            toast.success('Asset request submitted');
            setShowRequestModal(false);
            setRequestData({ category: '', description: '', priority: 'NORMAL', justification: '' });
            fetchRequests();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    const downloadRequestPDF = async (request) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        let currentY = 20;
        
        // --- Header / Logo ---
        if (company?.logo) {
            try {
                const logoUrl = `${process.env.REACT_APP_BACKEND_URL}${company.logo}`;
                const img = await createImage(logoUrl);
                
                // Calculate dimensions to fit in header
                const mw = 40;
                const mh = 20;
                let w = img.width;
                let h = img.height;
                const ratio = w / h;
                
                if (w > mw) {
                    w = mw;
                    h = mw / ratio;
                }
                if (h > mh) {
                    h = mh;
                    w = mh * ratio;
                }
                
                doc.addImage(img, 'PNG', margin, currentY, w, h);
                currentY += h + 10;
            } catch (err) {
                console.error("Failed to load logo for PDF", err);
                currentY += 10;
            }
        } else {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(company?.name || 'Company Name', margin, currentY);
            currentY += 10;
        }

        // --- Date (Top Right) ---
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(80);
        const requestDate = new Date(request.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
        doc.text(`Date: ${requestDate}`, pageWidth - margin, 20, { align: 'right' });

        // --- Formal "To" Address ---
        doc.setTextColor(0);
        doc.setFont('helvetica', 'bold');
        doc.text('To,', margin, currentY + 10);
        doc.setFont('helvetica', 'normal');
        doc.text('The IT Administration / Procurement Department', margin, currentY + 15);
        doc.text(company?.name || 'The Organization', margin, currentY + 20);
        currentY += 35;

        // --- Subject Line ---
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(`Subject: REQUISITION FOR ${request.category?.toUpperCase()} ASSET - ${request.priority?.toUpperCase()} PRIORITY`, margin, currentY);
        doc.line(margin, currentY + 1.5, pageWidth - margin, currentY + 1.5);
        currentY += 15;

        // --- Salutation & Body ---
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text('Dear Team,', margin, currentY);
        currentY += 8;

        const introText = `I am writing to formally submit a request for a new IT asset. Below are the specific details regarding the requirement for my professional duties:`;
        const splitIntro = doc.splitTextToSize(introText, pageWidth - (margin * 2));
        doc.text(splitIntro, margin, currentY);
        currentY += (splitIntro.length * 5) + 5;

        // --- Request Details List ---
        const details = [
            ['Requester Name', request.employee_name],
            ['Asset Category', request.category],
            ['Priority Level', request.priority],
            ['Current Status', request.status],
            ['Submitted On', new Date(request.created_at).toLocaleString()]
        ];

        autoTable(doc, {
            startY: currentY,
            body: details,
            theme: 'plain',
            styles: { fontSize: 10, cellPadding: 2 },
            columnStyles: {
                0: { fontStyle: 'bold', textColor: [100, 100, 100], cellWidth: 40 },
                1: { textColor: [0, 0, 0] }
            },
            margin: { left: margin }
        });

        currentY = doc.lastAutoTable.finalY + 10;

        // --- Description Section ---
        doc.setFont('helvetica', 'bold');
        doc.text('Requirement Description:', margin, currentY);
        doc.setFont('helvetica', 'normal');
        const descText = request.description || 'No specific description provided.';
        const splitDesc = doc.splitTextToSize(descText, pageWidth - (margin * 2));
        doc.text(splitDesc, margin, currentY + 6);
        currentY += (splitDesc.length * 5) + 12;

        // --- Justification Section ---
        if (request.justification) {
            doc.setFont('helvetica', 'bold');
            doc.text('Justification for Request:', margin, currentY);
            doc.setFont('helvetica', 'normal');
            const justificationText = request.justification;
            const splitJust = doc.splitTextToSize(justificationText, pageWidth - (margin * 2));
            doc.text(splitJust, margin, currentY + 6);
            currentY += (splitJust.length * 5) + 12;
        }

        // --- Closing ---
        const closingText = `I request you to kindly review this application and provide the necessary approval for the procurement/assignment of the mentioned asset.`;
        const splitClosing = doc.splitTextToSize(closingText, pageWidth - (margin * 2));
        doc.text(splitClosing, margin, currentY);
        currentY += (splitClosing.length * 5) + 15;

        doc.text('Sincerely,', margin, currentY);
        doc.setFont('helvetica', 'bold');
        doc.text(request.employee_name || 'The Requester', margin, currentY + 6);
        doc.setFont('helvetica', 'normal');
        doc.text('Employee', margin, currentY + 11);

        // --- Footer ---
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Note: This is a system-generated document and serves as a formal digital record of the request.', pageWidth / 2, doc.internal.pageSize.getHeight() - margin, { align: 'center' });

        doc.save(`Asset_Request_${request.id}.pdf`);
    };

    const openEditModal = (asset) => {
        setSelectedAsset(asset);
        setFormData({
            asset_tag: asset.asset_tag,
            name: asset.name,
            description: asset.description || '',
            category: asset.category,
            subcategory: asset.subcategory || '',
            brand: asset.brand || '',
            model: asset.model || '',
            serial_number: asset.serial_number || '',
            purchase_date: asset.purchase_date || '',
            purchase_price: asset.purchase_price || '',
            warranty_expiry: asset.warranty_expiry || '',
            condition: asset.condition,
            status: asset.status,
            location: asset.location || '',
            notes: asset.notes || '',
            vendor: asset.vendor || {},
            license_key: asset.license_key || '',
            license_type: asset.license_type || '',
            license_expiry: asset.license_expiry || '',
            license_seats: asset.license_seats || '',
        });
        setShowEditModal(true);
    };

    const openAssignModal = (asset) => {
        setSelectedAsset(asset);
        setAssignData({ employee_id: '', notes: '' });
        setShowAssignModal(true);
    };

    const openMaintenanceModal = (asset) => {
        setSelectedAsset(asset);
        setMaintenanceData({
            maintenance_type: '',
            description: '',
            cost: '',
            performed_by: '',
            performed_date: new Date().toISOString().split('T')[0],
            notes: ''
        });
        setShowMaintenanceModal(true);
    };

    const downloadTemplate = () => {
        const headers = ['Asset Tag', 'Name', 'Category', 'Description', 'Subcategory', 'Brand', 'Model', 'Serial Number', 'Purchase Date', 'Purchase Price', 'Warranty Expiry', 'Location', 'Notes'];
        const sampleRow = ['AST-001', 'MacBook Pro 14"', 'HARDWARE', 'Company laptop', 'Laptop', 'Apple', 'MacBook Pro 14-inch M3', 'ABC123XYZ', '2024-01-15', '200000', '2027-01-15', 'Office - Desk 12', 'Assigned to Engineering'];

        const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'asset_upload_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const getCategoryIcon = (category) => {
        const cat = assetCategories.find(c => c.value === category);
        return cat ? cat.icon : Package;
    };

    const getStatusBadge = (status) => {
        const st = ASSET_STATUSES.find(s => s.value === status);
        return (
            <Badge variant="outline" className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${st?.color || 'bg-gray-400'}`}></span>
                {st?.label || status}
            </Badge>
        );
    };

    const assetColumns = [
        {
            accessorKey: 'asset_tag',
            header: 'Asset Tag',
            cell: ({ row }) => (
                <Link
                    to={`/assets/${row.original.id}`}
                    className="font-mono font-medium text-primary hover:underline cursor-pointer"
                >
                    {row.original.asset_tag}
                </Link>
            ),
        },
        {
            accessorKey: 'name',
            header: 'Name',
            cell: ({ row }) => {
                const Icon = getCategoryIcon(row.original.category);
                return (
                    <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{row.original.name}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: 'category',
            header: 'Category',
            cell: ({ row }) => {
                const cat = assetCategories.find(c => c.value === row.original.category);
                return <Badge variant="secondary">{cat?.label || row.original.category}</Badge>;
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => getStatusBadge(row.original.status),
        },
        {
            accessorKey: 'assigned_to_name',
            header: 'Assigned To',
            cell: ({ row }) => {
                if (!row.original.assigned_to_name) return '-';
                return (
                    <div>
                        <span className="font-medium">{row.original.assigned_to_name}</span>
                        {row.original.assigned_to_code && (
                            <span className="text-xs text-muted-foreground block">{row.original.assigned_to_code}</span>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'condition',
            header: 'Condition',
            cell: ({ row }) => {
                const cond = ASSET_CONDITIONS.find(c => c.value === row.original.condition);
                return cond?.label || row.original.condition;
            },
        },
        {
            accessorKey: 'purchase_price',
            header: 'Value',
            cell: ({ row }) => row.original.purchase_price ? formatIndianCurrency(row.original.purchase_price) : '-',
        },
        {
            accessorKey: 'warranty_expiry',
            header: 'Warranty',
            cell: ({ row }) => {
                if (!row.original.warranty_expiry) return '-';
                const expiry = new Date(row.original.warranty_expiry);
                const today = new Date();
                const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
                const formattedDate = expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

                if (daysUntilExpiry < 0) {
                    return <span className="text-red-500">{formattedDate} (Expired)</span>;
                } else if (daysUntilExpiry <= 30) {
                    return <span className="text-yellow-600">{formattedDate} ({daysUntilExpiry}d left)</span>;
                }
                return formattedDate;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <TooltipProvider delayDuration={100}>
                    <div className="flex items-center gap-1">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => openEditModal(row.original)}>
                                    <Pencil className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit Asset</TooltipContent>
                        </Tooltip>
                        {row.original.status === 'AVAILABLE' && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" onClick={() => openAssignModal(row.original)}>
                                        <UserPlus className="h-4 w-4 text-green-600" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Assign to Employee</TooltipContent>
                            </Tooltip>
                        )}
                        {row.original.status === 'ASSIGNED' && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" onClick={() => handleUnassignAsset(row.original)}>
                                        <UserMinus className="h-4 w-4 text-orange-600" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Unassign Asset</TooltipContent>
                            </Tooltip>
                        )}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => handleViewHistory(row.original)}>
                                    <History className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>View History</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => openMaintenanceModal(row.original)}>
                                    <Hammer className="h-4 w-4 text-blue-600" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Add Maintenance</TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            ),
        },
    ];

    const requestColumns = [
        {
            accessorKey: 'employee_name',
            header: 'Requested By',
        },
        {
            accessorKey: 'category',
            header: 'Category',
            cell: ({ row }) => {
                const cat = assetCategories.find(c => c.value === row.original.category);
                return <Badge variant="secondary">{cat?.label || row.original.category}</Badge>;
            },
        },
        {
            accessorKey: 'description',
            header: 'Description',
        },
        {
            accessorKey: 'priority',
            header: 'Priority',
            cell: ({ row }) => {
                const colors = { LOW: 'bg-gray-500', NORMAL: 'bg-blue-500', HIGH: 'bg-orange-500', URGENT: 'bg-red-500' };
                return (
                    <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        <span className={`w-2 h-2 rounded-full ${colors[row.original.priority] || 'bg-gray-400'}`}></span>
                        {row.original.priority}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const colors = { PENDING: 'bg-yellow-500', APPROVED: 'bg-green-500', REJECTED: 'bg-red-500', FULFILLED: 'bg-blue-500' };
                return (
                    <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        <span className={`w-2 h-2 rounded-full ${colors[row.original.status] || 'bg-gray-400'}`}></span>
                        {row.original.status}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'created_at',
            header: 'Requested On',
            cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <TooltipProvider delayDuration={100}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedRequest(row.original); setShowRequestDetailModal(true); }}>
                                    <Search className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>View Details</TooltipContent>
                        </Tooltip>
                        {row.original.status === 'PENDING' && (
                            <>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button variant="ghost" size="sm" onClick={() => handleApproveRequest(row.original)} className="text-green-600">
                                            <CheckCircle className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Approve Request</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button variant="ghost" size="sm" onClick={() => handleRejectRequest(row.original)} className="text-red-600">
                                            <XCircle className="h-4 w-4" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Reject Request</TooltipContent>
                                </Tooltip>
                            </>
                        )}
                    </TooltipProvider>
                </div>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const pendingRequestsCount = requests.filter(r => r.status === 'PENDING').length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">IT Asset Management</h1>
                    <p className="text-muted-foreground">Track and manage company IT assets</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowBulkUploadModal(true)}>
                        <Upload className="h-4 w-4 mr-2" />
                        Bulk Upload
                    </Button>
                    <Button onClick={() => { setFormData({}); setShowAddModal(true); }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Asset
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col items-center text-center gap-2">
                                <div className="p-2 rounded-lg bg-blue-100">
                                    <Package className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.total_assets}</p>
                                    <p className="text-xs text-muted-foreground">Total Assets</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col items-center text-center gap-2">
                                <div className="p-2 rounded-lg bg-green-100">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.by_status?.AVAILABLE || 0}</p>
                                    <p className="text-xs text-muted-foreground">Available</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col items-center text-center gap-2">
                                <div className="p-2 rounded-lg bg-purple-100">
                                    <UserPlus className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.by_status?.ASSIGNED || 0}</p>
                                    <p className="text-xs text-muted-foreground">Assigned</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col items-center text-center gap-2">
                                <div className="p-2 rounded-lg bg-yellow-100">
                                    <Wrench className="h-5 w-5 text-yellow-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.in_repair}</p>
                                    <p className="text-xs text-muted-foreground">In Repair</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col items-center text-center gap-2">
                                <div className="p-2 rounded-lg bg-orange-100">
                                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.warranty_expiring_soon}</p>
                                    <p className="text-xs text-muted-foreground">Warranty Expiring</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-col items-center text-center gap-2">
                                <div className="p-2 rounded-lg bg-indigo-100">
                                    <TrendingUp className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-xl font-bold truncate">{formatIndianCurrency(stats.total_value || 0)}</p>
                                    <p className="text-xs text-muted-foreground">Total Value</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="assets" className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        All Assets
                    </TabsTrigger>
                    <TabsTrigger value="requests" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Requests
                        {pendingRequestsCount > 0 && (
                            <Badge variant="destructive" className="ml-1 h-5 min-w-5">{pendingRequestsCount}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="analytics" className="flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        Analytics
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="assets" className="space-y-4">
                    {/* Filters */}
                    <div className="flex flex-wrap gap-4">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search assets..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="All Categories" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                {assetCategories.map((cat) => (
                                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="All Statuses" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Statuses</SelectItem>
                                {ASSET_STATUSES.map((st) => (
                                    <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Assets Table */}


                    <DataTable
                        columns={assetColumns}
                        data={assets}
                        loading={loading}
                        emptyMessage="No assets found matching your criteria."
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        columnVisibility={columnVisibility}
                        setColumnVisibility={setColumnVisibility}
                        pagination={{
                            page,
                            pageSize,
                            total: totalAssets,
                            onPageChange: setPage,
                        }}
                    />

                </TabsContent>

                <TabsContent value="requests" className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Asset Requests</h3>
                        <Button onClick={() => setShowRequestModal(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            New Request
                        </Button>
                    </div>

                    <DataTable 
                        columns={requestColumns} 
                        data={requests} 
                        onRowClick={(req) => { setSelectedRequest(req); setShowRequestDetailModal(true); }}
                    />
                </TabsContent>

                <TabsContent value="analytics" className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Assets by Category</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {stats && Object.entries(stats.by_category || {}).map(([category, count]) => {
                                        const cat = assetCategories.find(c => c.value === category);
                                        const Icon = cat?.icon || Package;
                                        const percentage = stats.total_assets ? ((count / stats.total_assets) * 100).toFixed(1) : 0;
                                        return (
                                            <div key={category} className="flex items-center gap-3">
                                                <Icon className="h-5 w-5 text-muted-foreground" />
                                                <div className="flex-1">
                                                    <div className="flex justify-between mb-1">
                                                        <span className="text-sm font-medium">{cat?.label || category}</span>
                                                        <span className="text-sm text-muted-foreground">{count}</span>
                                                    </div>
                                                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-primary rounded-full transition-all"
                                                            style={{ width: `${percentage}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Assets by Status</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {stats && Object.entries(stats.by_status || {}).map(([status, count]) => {
                                        const st = ASSET_STATUSES.find(s => s.value === status);
                                        const percentage = stats.total_assets ? ((count / stats.total_assets) * 100).toFixed(1) : 0;
                                        return (
                                            <div key={status} className="flex items-center gap-3">
                                                <div className={`w-3 h-3 rounded-full ${st?.color || 'bg-gray-400'}`} />
                                                <div className="flex-1">
                                                    <div className="flex justify-between mb-1">
                                                        <span className="text-sm font-medium">{st?.label || status}</span>
                                                        <span className="text-sm text-muted-foreground">{count} ({percentage}%)</span>
                                                    </div>
                                                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full ${st?.color || 'bg-gray-400'} rounded-full transition-all`}
                                                            style={{ width: `${percentage}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Add/Edit Asset Modal */}
            <Dialog open={showAddModal || showEditModal} onOpenChange={(open) => { if (!open) { setShowAddModal(false); setShowEditModal(false); } }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{showEditModal ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={showEditModal ? handleUpdateAsset : handleAddAsset} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="asset_tag">Asset Tag *</Label>
                                <Input
                                    id="asset_tag"
                                    value={formData.asset_tag || ''}
                                    onChange={(e) => setFormData({ ...formData, asset_tag: e.target.value })}
                                    placeholder="e.g., AST-001"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name || ''}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., MacBook Pro 14 inch"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="category">Category *</Label>
                                <Select value={formData.category || ''} onValueChange={(v) => setFormData({ ...formData, category: v })} required>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {assetCategories.map((cat) => (
                                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="subcategory">Subcategory</Label>
                                <Input
                                    id="subcategory"
                                    value={formData.subcategory || ''}
                                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                                    placeholder="e.g., Laptop"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="brand">Brand</Label>
                                <Input
                                    id="brand"
                                    value={formData.brand || ''}
                                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                                    placeholder="e.g., Apple"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="model">Model</Label>
                                <Input
                                    id="model"
                                    value={formData.model || ''}
                                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                                    placeholder="e.g., MacBook Pro M3"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="serial_number">Serial Number</Label>
                                <Input
                                    id="serial_number"
                                    value={formData.serial_number || ''}
                                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                                    placeholder="e.g., ABC123XYZ"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="condition">Condition</Label>
                                <Select value={formData.condition || 'GOOD'} onValueChange={(v) => setFormData({ ...formData, condition: v })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ASSET_CONDITIONS.map((cond) => (
                                            <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {showEditModal && (
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status</Label>
                                    <Select value={formData.status || ''} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ASSET_STATUSES.map((st) => (
                                                <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="purchase_date">Purchase Date</Label>
                                <Input
                                    id="purchase_date"
                                    type="date"
                                    value={formData.purchase_date || ''}
                                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="purchase_price">Purchase Price (₹)</Label>
                                <Input
                                    id="purchase_price"
                                    type="number"
                                    value={formData.purchase_price || ''}
                                    onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) })}
                                    placeholder="0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="warranty_expiry">Warranty Expiry</Label>
                                <Input
                                    id="warranty_expiry"
                                    type="date"
                                    value={formData.warranty_expiry || ''}
                                    onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="location">Location</Label>
                                <Input
                                    id="location"
                                    value={formData.location || ''}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                    placeholder="e.g., Office Floor 2"
                                />
                            </div>
                        </div>

                        {/* Vendor Information */}
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                            <div className="col-span-2">
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">Vendor Information</h4>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="vendor_name">Vendor Name</Label>
                                <Input
                                    id="vendor_name"
                                    value={formData.vendor?.name || ''}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        vendor: { ...formData.vendor, name: e.target.value }
                                    })}
                                    placeholder="e.g., Dell Technologies"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="vendor_contact">Contact Person</Label>
                                <Input
                                    id="vendor_contact"
                                    value={formData.vendor?.contact_person || ''}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        vendor: { ...formData.vendor, contact_person: e.target.value }
                                    })}
                                    placeholder="Contact name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="vendor_email">Vendor Email</Label>
                                <Input
                                    id="vendor_email"
                                    type="email"
                                    value={formData.vendor?.email || ''}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        vendor: { ...formData.vendor, email: e.target.value }
                                    })}
                                    placeholder="vendor@example.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="vendor_phone">Vendor Phone</Label>
                                <Input
                                    id="vendor_phone"
                                    value={formData.vendor?.phone || ''}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        vendor: { ...formData.vendor, phone: e.target.value }
                                    })}
                                    placeholder="+91 9876543210"
                                />
                            </div>
                        </div>

                        {/* Software-specific fields */}
                        {formData.category === 'SOFTWARE' && (
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                <div className="space-y-2">
                                    <Label htmlFor="license_key">License Key</Label>
                                    <Input
                                        id="license_key"
                                        value={formData.license_key || ''}
                                        onChange={(e) => setFormData({ ...formData, license_key: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="license_type">License Type</Label>
                                    <Input
                                        id="license_type"
                                        value={formData.license_type || ''}
                                        onChange={(e) => setFormData({ ...formData, license_type: e.target.value })}
                                        placeholder="e.g., Perpetual, Subscription"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="license_expiry">License Expiry</Label>
                                    <Input
                                        id="license_expiry"
                                        type="date"
                                        value={formData.license_expiry || ''}
                                        onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="license_seats">License Seats</Label>
                                    <Input
                                        id="license_seats"
                                        type="number"
                                        value={formData.license_seats || ''}
                                        onChange={(e) => setFormData({ ...formData, license_seats: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Assignment Information */}
                        <div className="grid grid-cols-1 gap-4 pt-4 border-t">
                            <div className="col-span-1">
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">Direct Assignment (Optional)</h4>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="assigned_to">Assign to Employee</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            className="w-full justify-between font-normal"
                                        >
                                            {formData.assigned_to
                                                ? employees.find((emp) => emp.id === formData.assigned_to)
                                                    ? `${employees.find((emp) => emp.id === formData.assigned_to).first_name} ${employees.find((emp) => emp.id === formData.assigned_to).last_name} (${employees.find((emp) => emp.id === formData.assigned_to).employee_code})`
                                                    : "Select employee..."
                                                : "Keep Unassigned"}
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[400px] p-0" align="start">
                                        <Command>
                                            <CommandInput placeholder="Search employee..." />
                                            <CommandList>
                                                <CommandEmpty>No employee found.</CommandEmpty>
                                                <CommandGroup>
                                                    <CommandItem
                                                        value="unassigned"
                                                        onSelect={() => {
                                                            setFormData({ ...formData, assigned_to: null });
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                !formData.assigned_to ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        Keep Unassigned
                                                    </CommandItem>
                                                    {employees.map((emp) => (
                                                        <CommandItem
                                                            key={emp.id}
                                                            value={`${emp.first_name} ${emp.last_name} ${emp.employee_code}`}
                                                            onSelect={() => {
                                                                setFormData({ ...formData, assigned_to: emp.id });
                                                            }}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    formData.assigned_to === emp.id ? "opacity-100" : "opacity-0"
                                                                )}
                                                            />
                                                            {emp.first_name} {emp.last_name} ({emp.employee_code})
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                <p className="text-[11px] text-muted-foreground italic mt-1.5">
                                    Search by name or code. If assigned, status will set to "ASSIGNED"
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="assigned_notes">Assignment Notes</Label>
                                <Textarea
                                    id="assigned_notes"
                                    value={formData.assigned_notes || ''}
                                    onChange={(e) => setFormData({ ...formData, assigned_notes: e.target.value })}
                                    placeholder="Reason for assignment, return policy notes, etc."
                                    rows={2}
                                />
                            </div>
                        </div>

                        <div className="space-y-2 pt-4 border-t">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                value={formData.description || ''}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Additional details about the asset..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="notes">Notes</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes || ''}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Internal notes..."
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {showEditModal ? 'Update' : 'Create'} Asset
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Assign Asset Modal */}
            <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Assign Asset</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAssignAsset} className="space-y-4">
                        <div className="p-3 bg-muted rounded-lg">
                            <p className="font-medium">{selectedAsset?.name}</p>
                            <p className="text-sm text-muted-foreground">{selectedAsset?.asset_tag}</p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="employee">Assign To *</Label>
                            <Select value={assignData.employee_id} onValueChange={(v) => setAssignData({ ...assignData, employee_id: v })} required>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Employee" />
                                </SelectTrigger>
                                <SelectContent>
                                    {employees.filter(e => e.status === 'ACTIVE').map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id}>
                                            {emp.first_name} {emp.last_name} ({emp.employee_code})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="assign_notes">Notes</Label>
                            <Textarea
                                id="assign_notes"
                                value={assignData.notes}
                                onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                                placeholder="Assignment notes..."
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                            <Button type="submit" disabled={submitting || !assignData.employee_id}>
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Assign Asset
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* History Modal */}
            <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Asset History - {selectedAsset?.name}</DialogTitle>
                    </DialogHeader>
                    <Tabs defaultValue="assignments">
                        <TabsList>
                            <TabsTrigger value="assignments">Assignment History</TabsTrigger>
                            <TabsTrigger value="maintenance">Maintenance History</TabsTrigger>
                        </TabsList>
                        <TabsContent value="assignments" className="max-h-[400px] overflow-y-auto">
                            {assetHistory.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No assignment history found</p>
                            ) : (
                                <div className="space-y-3">
                                    {assetHistory.map((record) => (
                                        <div key={record.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                                            <div className={`p-2 rounded-full ${record.action === 'ASSIGNED' ? 'bg-green-500/20' : 'bg-orange-500/20'}`}>
                                                {record.action === 'ASSIGNED' ? (
                                                    <UserPlus className="h-4 w-4 text-green-600" />
                                                ) : (
                                                    <UserMinus className="h-4 w-4 text-orange-600" />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium">
                                                    {record.action === 'ASSIGNED' ? 'Assigned to' : 'Unassigned from'} {record.employee_name}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {new Date(record.created_at).toLocaleDateString()} at {new Date(record.created_at).toLocaleTimeString()}
                                                </p>
                                                {record.notes && <p className="text-sm mt-1">{record.notes}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="maintenance" className="max-h-[400px] overflow-y-auto">
                            {maintenanceHistory.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No maintenance history found</p>
                            ) : (
                                <div className="space-y-3">
                                    {maintenanceHistory.map((record) => (
                                        <div key={record.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                                            <div className="p-2 rounded-full bg-blue-500/20">
                                                <Wrench className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between">
                                                    <p className="font-medium">{record.maintenance_type}</p>
                                                    {record.cost && <span className="text-sm font-medium">{formatIndianCurrency(record.cost)}</span>}
                                                </div>
                                                <p className="text-sm">{record.description}</p>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    {new Date(record.performed_date).toLocaleDateString()}
                                                    {record.performed_by && ` · By ${record.performed_by}`}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </DialogContent>
            </Dialog>

            {/* Maintenance Modal */}
            <Dialog open={showMaintenanceModal} onOpenChange={setShowMaintenanceModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Maintenance Record</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddMaintenance} className="space-y-4">
                        <div className="p-3 bg-muted rounded-lg">
                            <p className="font-medium">{selectedAsset?.name}</p>
                            <p className="text-sm text-muted-foreground">{selectedAsset?.asset_tag}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="maintenance_type">Type *</Label>
                                <Select value={maintenanceData.maintenance_type || ''} onValueChange={(v) => setMaintenanceData({ ...maintenanceData, maintenance_type: v })} required>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MAINTENANCE_TYPES.map((type) => (
                                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="performed_date">Performed Date *</Label>
                                <Input
                                    id="performed_date"
                                    type="date"
                                    value={maintenanceData.performed_date || ''}
                                    onChange={(e) => setMaintenanceData({ ...maintenanceData, performed_date: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="performed_by">Performed By</Label>
                                <Input
                                    id="performed_by"
                                    value={maintenanceData.performed_by || ''}
                                    onChange={(e) => setMaintenanceData({ ...maintenanceData, performed_by: e.target.value })}
                                    placeholder="Technician name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="cost">Cost (₹)</Label>
                                <Input
                                    id="cost"
                                    type="number"
                                    value={maintenanceData.cost || ''}
                                    onChange={(e) => setMaintenanceData({ ...maintenanceData, cost: parseFloat(e.target.value) })}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="maint_description">Description *</Label>
                            <Textarea
                                id="maint_description"
                                value={maintenanceData.description || ''}
                                onChange={(e) => setMaintenanceData({ ...maintenanceData, description: e.target.value })}
                                placeholder="Describe the maintenance performed..."
                                required
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowMaintenanceModal(false)}>Cancel</Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Add Record
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Bulk Upload Modal */}
            <Dialog open={showBulkUploadModal} onOpenChange={setShowBulkUploadModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Bulk Upload Assets</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleBulkUpload} className="space-y-4">
                        <div className="flex justify-center">
                            <Button type="button" variant="outline" onClick={downloadTemplate}>
                                <Download className="h-4 w-4 mr-2" />
                                Download Template
                            </Button>
                        </div>
                        <div className="border-2 border-dashed rounded-lg p-6 text-center">
                            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground mb-2">
                                Upload Excel or CSV file with asset data
                            </p>
                            <Input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                className="max-w-xs mx-auto"
                            />
                            {uploadFile && (
                                <p className="mt-2 text-sm font-medium">{uploadFile.name}</p>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowBulkUploadModal(false)}>Cancel</Button>
                            <Button type="submit" disabled={submitting || !uploadFile}>
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Upload Assets
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Asset Request Modal */}
            <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Request New Asset</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateRequest} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="req_category">Asset Category *</Label>
                            <Select value={requestData.category} onValueChange={(v) => setRequestData({ ...requestData, category: v })} required>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {assetCategories.map((cat) => (
                                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="req_priority">Priority</Label>
                            <Select value={requestData.priority} onValueChange={(v) => setRequestData({ ...requestData, priority: v })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="LOW">Low</SelectItem>
                                    <SelectItem value="NORMAL">Normal</SelectItem>
                                    <SelectItem value="HIGH">High</SelectItem>
                                    <SelectItem value="URGENT">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="req_description">Description *</Label>
                            <Textarea
                                id="req_description"
                                value={requestData.description}
                                onChange={(e) => setRequestData({ ...requestData, description: e.target.value })}
                                placeholder="Describe what you need..."
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="req_justification">Justification</Label>
                            <Textarea
                                id="req_justification"
                                value={requestData.justification}
                                onChange={(e) => setRequestData({ ...requestData, justification: e.target.value })}
                                placeholder="Why do you need this asset?"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Submit Request
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Request Detail Modal */}
            <Dialog open={showRequestDetailModal} onOpenChange={setShowRequestDetailModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <div className="flex justify-between items-center pr-8">
                            <DialogTitle>Asset Request Details</DialogTitle>
                            <Button variant="outline" size="sm" onClick={() => downloadRequestPDF(selectedRequest)}>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                            </Button>
                        </div>
                    </DialogHeader>
                    {selectedRequest && (
                        <div className="space-y-6 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground uppercase">Requested By</Label>
                                    <p className="font-medium">{selectedRequest.employee_name}</p>
                                </div>
                                <div className="space-y-1 text-right">
                                    <Label className="text-xs text-muted-foreground uppercase">Requested On</Label>
                                    <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground uppercase">Category</Label>
                                    <div>
                                        <Badge variant="secondary">
                                            {assetCategories.find(c => c.value === selectedRequest.category)?.label || selectedRequest.category}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="space-y-1 text-right">
                                    <Label className="text-xs text-muted-foreground uppercase">Status</Label>
                                    <div className="flex justify-end">
                                        {(() => {
                                            const colors = { PENDING: 'bg-yellow-500', APPROVED: 'bg-green-500', REJECTED: 'bg-red-500', FULFILLED: 'bg-blue-500' };
                                            return (
                                                <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                                    <span className={`w-2 h-2 rounded-full ${colors[selectedRequest.status] || 'bg-gray-400'}`}></span>
                                                    {selectedRequest.status}
                                                </Badge>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground uppercase">Priority</Label>
                                    <div>
                                        {(() => {
                                            const colors = { LOW: 'bg-gray-500', NORMAL: 'bg-blue-500', HIGH: 'bg-orange-500', URGENT: 'bg-red-500' };
                                            return (
                                                <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                                    <span className={`w-2 h-2 rounded-full ${colors[selectedRequest.priority] || 'bg-gray-400'}`}></span>
                                                    {selectedRequest.priority}
                                                </Badge>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground uppercase">Description</Label>
                                <div className="p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">
                                    {selectedRequest.description}
                                </div>
                            </div>

                            {selectedRequest.justification && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground uppercase">Justification</Label>
                                    <div className="p-3 bg-muted/50 border border-border rounded-md text-sm italic">
                                        "{selectedRequest.justification}"
                                    </div>
                                </div>
                            )}

                            {selectedRequest.status === 'PENDING' && (
                                <div className="flex gap-3 justify-end items-center pt-2">
                                    <Button variant="outline" onClick={() => handleRejectRequest(selectedRequest)} className="text-red-600 border-red-200 hover:bg-red-50">
                                        <XCircle className="h-4 w-4 mr-2" />
                                        Reject Request
                                    </Button>
                                    <Button onClick={() => handleApproveRequest(selectedRequest)} className="bg-green-600 hover:bg-green-700">
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Approve Request
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmationDialog {...dialogProps} />
        </div >
    );
}

export default AssetManagement;
